import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SharedModule } from '@aiwyn/shared';
import {
  EntityType,
  UploadDataReference,
  UploadFileJob,
  UploadFilesModule,
  UploadType,
} from '../../../../../../shared/components/upload-files';
import { CrlRequestItem } from '../../../../models';

/**
 * Per-item file upload area (PP-3753 building block 3, REQ-5/REQ-9).
 *
 * Reuses the shared `awn-upload-files` dropzone. Scoped to the CLIENT_REQUEST_SUBMISSION
 * upload context, the dropzone hits the single-call `submission-file-stream`
 * endpoint, which uploads the file bytes AND links them to the request item
 * server-side in one request. This component owns only the dropzone: it hands the
 * job its `entityData` and nothing else. Everything downstream — the toast, the
 * Documents tab's per-file rows, and the reload that surfaces a finished upload —
 * reads the job from `DmsIntegrationState`, whose records outlive this component.
 * Uploads raise no snackbars of their own; the toast owns that feedback.
 *
 * Replace File is a dedicated hidden `awn-upload-files` scoped to the
 * CLIENT_REQUEST_REPLACE context: its single-call `submission-file-replace-stream`
 * endpoint uploads the replacement bytes AND supersedes/versions the flagged
 * original server-side in one request. The flagged file's FILE id is captured
 * from the container's Replace trigger into `replaceOriginalFileId`, which the
 * replace dropzone's `entityData` carries.
 * TODO(PP-3642 follow-up): the filename-mismatch confirmation dialog is deferred,
 * matching the live legacy procedure (its dialog is currently disabled).
 */
@Component({
  selector: 'cxp-crl-panel-file-upload',
  templateUrl: './crl-panel-file-upload.component.html',
  styleUrls: ['./crl-panel-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, UploadFilesModule],
})
export class CrlPanelFileUploadComponent {
  readonly item = input.required<CrlRequestItem>();
  readonly requestId = input<number | null>(null);
  /**
   * Whether the item is still in an upload-allowed status (open / overdue /
   * changes-requested), computed by the selector. When false — in review or any
   * done state — the dropzone is hidden, so the only way to add files again is
   * Undo "I'm Finished". Defaults to false so a missing binding fails closed.
   */
  readonly canUpload = input<boolean>(false);
  /**
   * True while a status-moving action is in flight. The dropzone stays on screen
   * but stops accepting files — adding one mid-submit would race the very call
   * deciding whether the item still takes files.
   */
  readonly isSubmitting = input<boolean>(false);
  /**
   * Container-driven Replace File trigger: each emission carries the flagged
   * file's FILE id. Captured, then the dedicated (DMS-only) replace picker opens.
   */
  readonly replaceTrigger$ = input<Observable<{ originalFileId: number }>>();

  /** DMS entity reference: single-call CRL submission scoped to this item (add path). */
  protected readonly entityData = computed<UploadDataReference>(() => {
    const requestId = this.requestId() ?? 0;
    return {
      uploadType: UploadType.CLIENT_REQUEST_SUBMISSION,
      entityId: requestId,
      entityIdType: EntityType.JOB,
      clientRequestId: requestId,
      requestItemIds: [this.item().id],
      skipCategorization: false,
    };
  });

  /** FILE id being replaced, captured on the container trigger; feeds `replaceEntityData`. */
  private readonly replaceOriginalFileId = signal<number | null>(null);

  /**
   * DMS entity reference for the single-call replace: the endpoint uploads the
   * replacement AND supersedes/versions the flagged original in one request. Only
   * valid once a Replace is in flight — `originalFileId` is captured from the
   * container trigger; the picker only opens after it is set, so the upload always
   * carries the real id.
   */
  protected readonly replaceEntityData = computed<UploadDataReference>(() => {
    const requestId = this.requestId() ?? 0;
    return {
      uploadType: UploadType.CLIENT_REQUEST_REPLACE,
      entityId: requestId,
      entityIdType: EntityType.JOB,
      clientRequestId: requestId,
      clientRequestItemId: this.item().id,
      originalFileId: this.replaceOriginalFileId() ?? 0,
      skipCategorization: false,
    };
  });

  /** Opens the hidden replace dropzone's native picker (driven from `replaceTrigger$`). */
  protected readonly replacePicker$ = new Subject<void>();

  constructor() {
    // The container emits {originalFileId} on Replace: capture it into the signal
    // (so `replaceEntityData` carries it), then open the dedicated replace picker.
    effect((onCleanup) => {
      const trigger$ = this.replaceTrigger$();
      if (!trigger$) return;
      const subscription = trigger$.subscribe(({ originalFileId }) => {
        this.replaceOriginalFileId.set(originalFileId);
        this.replacePicker$.next();
      });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  /**
   * Replace dropzone finished its upload + supersede. Releases the captured file
   * id so the hidden picker cannot fire a second replacement at the same
   * original. Surfacing the new file is the page's reconciliation; progress,
   * success and failure belong to the toast and the Documents tab.
   */
  onReplaceUploadEnd(jobs: UploadFileJob[]): void {
    if (jobs.length === 0) return;
    this.replaceOriginalFileId.set(null);
  }
}
